import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Blueprint, ArrowRight, Upload, Lightning, Table, FileXls, Buildings, Ruler, Door, WindowsLogo, DownloadSimple, Monitor, DeviceMobile, X } from '@phosphor-icons/react';

const steps = [
  { num: '01', title: 'Upload Drawing', desc: 'Drag & drop CAD (DXF/DWG), PDF, or image files of your construction drawings', icon: Upload },
  { num: '02', title: 'AI Analysis', desc: 'GPT-5.2 Vision detects walls, doors, windows, columns and extracts dimensions', icon: Lightning },
  { num: '03', title: 'BOQ Generation', desc: 'Automatic Bill of Quantities with categories, units, and measurements', icon: Table },
  { num: '04', title: 'Export & Share', desc: 'Download professional Excel reports ready for contractors and clients', icon: FileXls },
];

const capabilities = [
  { title: 'Walls', desc: 'Area in m2, material type, finish', icon: Buildings },
  { title: 'Doors', desc: 'Count, dimensions, swing type', icon: Door },
  { title: 'Windows', desc: 'Count, size, frame material', icon: WindowsLogo },
  { title: 'Dimensions', desc: 'Room sizes, perimeter, area', icon: Ruler },
];

const plans = [
  {
    name: 'Starter',
    price: '199',
    plan_id: 'starter',
    desc: 'For individual professionals',
    features: ['5 Projects / month', '20 Drawing analyses', 'Excel export', 'Email support'],
    cta: 'Get Started',
    highlight: false,
  },
  {
    name: 'Professional',
    price: '499',
    plan_id: 'professional',
    desc: 'For growing teams',
    features: ['Unlimited Projects', '100 Drawing analyses', 'Excel + PDF export', 'Priority support', 'Team collaboration'],
    cta: 'Get Started',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: '2,000+',
    plan_id: 'enterprise',
    desc: 'Custom solutions',
    features: ['Unlimited everything', 'Custom AI training', 'API access', 'Dedicated support', 'BIM integration', 'SLA guarantee'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

export default function LandingPage() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="landing-page">
      {/* Navbar */}
      <nav className="bg-white border-b border-black/15 h-16 flex items-center justify-between px-6 md:px-12 sticky top-0 z-50" data-testid="landing-nav">
        <div className="flex items-center gap-2">
          <Blueprint size={28} weight="bold" className="text-[#FF5A00]" />
          <span className="font-sans text-xl font-black tracking-tight">DHR</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8F8F8F] ml-1">Platform</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleInstall}
            data-testid="landing-install-btn"
            className="inline-flex items-center gap-2 border border-black text-black px-4 py-2 font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-all duration-150"
          >
            <DownloadSimple size={16} weight="bold" />
            Install App
          </button>
          <Link
            to="/login"
            data-testid="nav-login-btn"
            className="font-mono text-sm uppercase tracking-wide text-[#5E5E5E] hover:text-[#FF5A00] transition-colors"
          >
            Sign In
          </Link>
          <Link
            to="/login"
            data-testid="nav-get-started-btn"
            className="bg-[#FF5A00] text-white px-6 py-2 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)]"
          >
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/a9207fd0-61a6-4ae1-883b-775aa3278bb1/images/8ac960c6dfbc296a0eb6d9fd3de62341d59f27936b279eea8c8825914e172687.png)` }}
        />
        <div className="relative py-24 md:py-40 px-6 md:px-12 max-w-6xl mx-auto">
          <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-6">
            AI-Powered Construction Analysis
          </p>
          <h1 className="font-sans text-5xl md:text-6xl font-black tracking-tighter leading-none text-[#050505] max-w-4xl">
            Transform Drawings Into Precise Bill of Quantities
          </h1>
          <p className="font-mono text-sm md:text-base text-[#5E5E5E] mt-6 max-w-2xl leading-relaxed">
            Upload architectural drawings and let GPT-5.2 Vision detect walls, doors, windows, 
            columns &mdash; extract dimensions, generate BOQ, and export professional reports instantly.
          </p>
          <div className="flex flex-wrap gap-4 mt-10">
            <Link
              to="/login"
              data-testid="hero-get-started-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-8 py-4 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none shadow-[4px_4px_0px_rgba(0,0,0,1)]"
            >
              Get Started <ArrowRight size={18} weight="bold" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-learn-more-btn"
              className="inline-flex items-center gap-2 border border-black text-black px-8 py-4 font-mono text-sm uppercase tracking-wide hover:bg-black hover:text-white transition-all duration-150"
            >
              Learn More
            </a>
          </div>
          <div className="flex flex-wrap gap-8 mt-16 pt-8 border-t border-black/15">
            {[
              ['150+', 'Projects Analyzed'],
              ['99.2%', 'Detection Accuracy'],
              ['50+', 'Companies Trust Us'],
            ].map(([val, label]) => (
              <div key={label}>
                <p className="font-sans text-3xl font-black text-[#050505]">{val}</p>
                <p className="font-mono text-xs uppercase tracking-wider text-[#8F8F8F] mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 md:py-32 px-6 md:px-12 border-t border-black/15" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-4">Process</p>
          <h2 className="font-sans text-3xl md:text-4xl font-black tracking-tight leading-none mb-16">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 border border-black/15">
            {steps.map(({ num, title, desc, icon: Icon }, i) => (
              <div key={num} className={`p-6 md:p-8 ${i < 3 ? 'md:border-r border-b md:border-b-0 border-black/15' : ''}`}>
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-[#FF5A00]">{num}</span>
                <Icon size={32} weight="regular" className="text-[#050505] mt-4 mb-3" />
                <h3 className="font-sans text-xl font-bold tracking-tight mb-2">{title}</h3>
                <p className="font-mono text-xs text-[#5E5E5E] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white border-t border-black/15" data-testid="capabilities-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12">
            <div className="md:col-span-5">
              <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-4">Detection</p>
              <h2 className="font-sans text-3xl md:text-4xl font-black tracking-tight leading-none mb-6">
                What We Detect
              </h2>
              <p className="font-mono text-sm text-[#5E5E5E] leading-relaxed mb-8">
                Our AI engine identifies and measures every architectural element in your drawings with professional precision.
              </p>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/a9207fd0-61a6-4ae1-883b-775aa3278bb1/images/fccdc4f687c508b22d4ebc2e97033bd5dd34421edd4282fe598235d987eb31e8.png"
                alt="Architecture analysis"
                className="w-full border border-black/15"
              />
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 border border-black/15">
                {capabilities.map(({ title, desc, icon: Icon }, i) => (
                  <div key={title} className={`p-6 md:p-8 ${i < 2 ? 'border-b border-black/15' : ''} ${i % 2 === 0 ? 'sm:border-r border-black/15' : ''}`}>
                    <Icon size={28} weight="regular" className="text-[#FF5A00] mb-4" />
                    <h3 className="font-sans text-lg font-bold tracking-tight mb-2">{title}</h3>
                    <p className="font-mono text-xs text-[#5E5E5E] leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-6 border border-black/15 bg-[#F7F7F7]">
                <p className="font-mono text-xs text-[#5E5E5E]">
                  <span className="font-bold text-black">Also detects:</span> Columns, Stairs, Ceiling heights, Room names, Notes, Floor areas, Firestop points, Electrical outlets
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-black/15" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-xs font-bold tracking-[0.2em] uppercase text-[#FF5A00] mb-4">Pricing</p>
          <h2 className="font-sans text-3xl md:text-4xl font-black tracking-tight leading-none mb-16">Simple, Transparent Plans</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 border border-black/15">
            {plans.map((plan, i) => (
              <div
                key={plan.name}
                className={`p-6 md:p-8 flex flex-col ${i < 2 ? 'md:border-r border-b md:border-b-0 border-black/15' : ''} ${plan.highlight ? 'bg-black text-white' : ''}`}
                data-testid={`pricing-${plan.name.toLowerCase()}`}
              >
                <p className={`font-mono text-xs font-bold tracking-[0.2em] uppercase ${plan.highlight ? 'text-[#FF5A00]' : 'text-[#8F8F8F]'}`}>
                  {plan.name}
                </p>
                <div className="mt-4 mb-2">
                  <span className="font-sans text-4xl font-black">{plan.price}</span>
                  <span className={`font-mono text-sm ${plan.highlight ? 'text-white/60' : 'text-[#8F8F8F]'}`}> SAR/mo</span>
                </div>
                <p className={`font-mono text-xs ${plan.highlight ? 'text-white/60' : 'text-[#5E5E5E]'} mb-6`}>{plan.desc}</p>
                <ul className="space-y-3 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2">
                      <span className={`font-mono text-xs ${plan.highlight ? 'text-[#FF5A00]' : 'text-[#FF5A00]'}`}>+</span>
                      <span className={`font-mono text-xs ${plan.highlight ? 'text-white/80' : 'text-[#5E5E5E]'}`}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/login"
                  data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                  className={`mt-8 block text-center py-3 px-6 font-mono text-sm uppercase tracking-wide transition-all duration-150 ${
                    plan.highlight
                      ? 'bg-[#FF5A00] text-white hover:bg-[#E04E00] shadow-[4px_4px_0px_rgba(255,255,255,0.3)]'
                      : 'border border-black text-black hover:bg-black hover:text-white'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-24 md:py-32 px-6 md:px-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-sans text-3xl md:text-4xl font-black tracking-tight leading-none text-white mb-6">
            Start Analyzing Your Drawings Today
          </h2>
          <p className="font-mono text-sm text-white/60 mb-10 max-w-xl mx-auto">
            Join 50+ companies already saving time and reducing errors with AI-powered BOQ generation.
          </p>
          <Link
            to="/login"
            data-testid="cta-get-started-btn"
            className="inline-flex items-center gap-2 bg-[#FF5A00] text-white px-8 py-4 font-mono text-sm uppercase tracking-wide hover:bg-[#E04E00] transition-all duration-150 shadow-[4px_4px_0px_rgba(255,255,255,0.3)]"
          >
            Get Started Free <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-black/15 py-8 px-6 md:px-12" data-testid="footer">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Blueprint size={20} weight="bold" className="text-[#FF5A00]" />
            <span className="font-sans text-sm font-black">DHR Platform</span>
          </div>
          <p className="font-mono text-xs text-[#8F8F8F]">
            &copy; {new Date().getFullYear()} DHR Platform. AI-powered construction analysis.
          </p>
        </div>
      </footer>

      {/* Install Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="landing-install-guide">
          <div className="bg-white border border-black/15 w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-black/15">
              <h3 className="font-sans text-lg font-bold tracking-tight">Install DHR Platform</h3>
              <button onClick={() => setShowGuide(false)} data-testid="close-landing-guide" className="text-[#8F8F8F] hover:text-black">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-start gap-3 p-4 bg-[#F7F7F7] border border-black/10">
                <Monitor size={24} weight="regular" className="text-[#FF5A00] shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-sm font-bold mb-1">Chrome / Edge (Desktop)</p>
                  <ol className="font-mono text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside">
                    <li>Click the <strong>install icon</strong> in the address bar (right side)</li>
                    <li>Or click <strong>Menu (&#8942;)</strong> &rarr; <strong>"Install DHR Platform"</strong></li>
                    <li>Click <strong>Install</strong> to confirm</li>
                  </ol>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-[#F7F7F7] border border-black/10">
                <DeviceMobile size={24} weight="regular" className="text-[#FF5A00] shrink-0 mt-0.5" />
                <div>
                  <p className="font-sans text-sm font-bold mb-1">Mobile (iOS / Android)</p>
                  <ol className="font-mono text-xs text-[#5E5E5E] space-y-1.5 list-decimal list-inside">
                    <li><strong>Android:</strong> Chrome &rarr; Menu (&#8942;) &rarr; "Add to Home Screen"</li>
                    <li><strong>iPhone:</strong> Safari &rarr; Share button &rarr; "Add to Home Screen"</li>
                  </ol>
                </div>
              </div>
              <p className="font-mono text-[10px] text-[#8F8F8F] text-center">The app will open as a standalone window like a native application</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
